import { motion } from 'framer-motion';
import { ShieldCheck, BadgeCheck, Eye, Lock, Heart } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
const TrustSafetySection = ({ language = 'ar', isDark = false }) => {
  const { localize } = useLanguage();
  const isAr = language === 'ar';

  const pillars = [
    {
      icon: BadgeCheck,
      title: localize({ ar: 'مدرسون موثقون بالكامل', en: 'Fully verified tutors' }),
      desc: localize({ ar: 'تحقق من الهوية والشهادات قبل الانضمام', en: 'ID and credentials verified before onboarding' }),
    },
    {
      icon: Eye,
      title: localize({ ar: 'متابعة أولياء الأمور', en: 'Parent oversight' }),
      desc: localize({ ar: 'لوحة متابعة كاملة لكل درس ومحادثة', en: 'Full dashboard monitoring every lesson & chat' }),
    },
    {
      icon: ShieldCheck,
      title: localize({ ar: 'خيارات تعلم آمنة', en: 'Safe learning options' }),
      desc: localize({ ar: 'منزل، أونلاين، أو مركز معتمد — أنت تختار', en: 'Home, online, or vetted centres — you choose' }),
    },
    {
      icon: Lock,
      title: localize({ ar: 'حماية بيانات متقدمة', en: 'Advanced data protection' }),
      desc: localize({ ar: 'تشفير بمعايير البنوك وخصوصية صارمة', en: 'Bank-grade encryption and strict privacy' }),
    },
  ];

  return (
    <section
      className={`relative py-10 sm:py-16 lg:py-28 overflow-hidden ${isDark ? 'bg-[#0b1120]' : 'bg-navy-800'}`}
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="trust-safety-section"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden
           style={{
             background:
               'radial-gradient(500px 300px at 15% 10%, rgba(99,102,241,0.25), transparent 60%),' +
               'radial-gradient(500px 300px at 85% 90%, rgba(245,158,11,0.15), transparent 60%)',
           }}
      />

      <div className="relative max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <div className="inline-flex items-center gap-2 bg-indigo-500/20 text-indigo-200 text-xs font-bold px-4 py-1.5 rounded-full border border-indigo-400/30 mb-5">
            <ShieldCheck className="w-3.5 h-3.5" />
            {localize({ ar: 'الأمان أولاً', en: 'Safety first' })}
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight">
            {localize({ ar: 'تعلّم بثقة وأمان', en: 'Learn with trust & safety' })}
          </h2>
          <p className="mt-4 text-base text-slate-300 leading-relaxed">
            {localize({ ar: 'نحن بنينا Learn With Trust من أجل طمأنتك. كل مدرّس، كل درس، وكل محادثة مبنية على الثقة.', en: 'We built Learn With Trust around your peace of mind. Every tutor, lesson, and chat is built on trust.' })}
          </p>
        </motion.div>

        {/* Pillars grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors"
                data-testid={`trust-pillar-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-indigo-300" />
                </div>
                <h3 className="text-sm font-bold text-white mb-1.5">{p.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Pinned highlight */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative rounded-2xl p-6 lg:p-7 bg-gradient-to-r from-amber-500/10 via-amber-400/5 to-transparent border border-amber-400/20 flex items-center gap-5"
        >
          <div className="w-12 h-12 rounded-2xl bg-amber-400/20 flex items-center justify-center flex-shrink-0">
            <Heart className="w-6 h-6 text-amber-300 fill-amber-300/60" />
          </div>
          <div>
            <h3 className="text-lg lg:text-xl font-bold text-white mb-0.5">
              {localize({ ar: 'سلامة طفلك أولويتنا', en: "Your child's safety is our #1 priority" })}
            </h3>
            <p className="text-xs lg:text-sm text-slate-300">
              {localize({ ar: 'نظام TED AI يراقب كل محادثة لحظة بلحظة، ويُنبّهك فوراً عند أي مخاطر.', en: 'TED AI monitors every chat in real time and alerts you immediately on any risk.' })}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustSafetySection;
